import { Button, Group, Radio, Stack, Text } from '@mantine/core'
import { closeAllModals, openModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  TExportDashboardToCSVFields,
  exportDashboardTOCSV,
  exportDashboardToCSVSchema,
} from 'api/dashboards/exportDashboardToCSV'
import { TDashboard } from 'api/dashboards/getDashboard'
import { InputDatetimePicker } from 'components/form/input/Input_Datetime'
import dayjs from 'dayjs'
import { useFormMutation } from 'hooks/useFormMutation'
import { useState } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BsCalendar2Date } from 'react-icons/bs'
import { FaFileCsv } from 'react-icons/fa'
import { TiWeatherPartlySunny } from 'react-icons/ti'
import { useParams } from 'react-router-dom'
import { capitalizeFirstLetter } from 'shared/utils'
import { HookFormError, IconButton, InputAsyncSelect, InputSubmit } from '..'

export const ExportDashboardToCSVButton = ({
  id,
  name,
  fromDate,
  toDate,
  dashboard,
}: {
  id: string
  name: string
  fromDate?: string
  toDate?: string
  dashboard: TDashboard
}) => {
  const { t } = useTranslation()
  const { deviceId } = useParams()

  const onClick = () => {
    openModal({
      title: t('dashboard.exportToCSVFile'),
      size: 'lg',
      children: (
        <ExportDashboardToCSVForm
          mutationFn={exportDashboardTOCSV({
            deviceId: deviceId!,
            dashboardId: id!,
            dashboardName: name!,
          })}
          fromDate={fromDate}
          toDate={toDate}
          dashboard={dashboard}
        />
      ),
    })
  }

  return (
    <IconButton
      size={'lg'}
      onClick={onClick}
      icon={FaFileCsv}
      label={t('dashboard.exportToCSVFile')}
    />
  )
}

export const ExportDashboardToCSVForm = ({
  mutationFn,
  fromDate,
  toDate,
  dashboard,
}: {
  mutationFn: (data: TExportDashboardToCSVFields) => Promise<unknown>
  fromDate?: string
  toDate?: string
  dashboard: TDashboard
}) => {
  const { t } = useTranslation()

  const { methods, onSubmit } = useFormMutation({
    mutationFn,
    schema: exportDashboardToCSVSchema,
    onSuccess: () => {
      closeAllModals()
    },
    onError: () => {
      showNotification({
        color: 'red',
        message: t('generic.errorOccured'),
      })
    },
    initial: {
      fromDate,
      toDate,
      exportMode: 'single',
    },
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <ExportDashboardToCSVFormFields dashboard={dashboard} />
      </form>
    </FormProvider>
  )
}

export const ExportDashboardToCSVFormFields = ({
  dashboard,
}: {
  dashboard: TDashboard
}) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext()
  const [exportMode, setExportMode] = useState<'all' | 'single'>('single')

  // Get unique indicators from all dashboard columns
  const getIndicatorOptions = async () => {
    const indicators = new Set<string>()
    dashboard.rows.forEach(row => {
      row.columns.forEach(col => {
        if (col.indicator) {
          indicators.add(col.indicator)
        }
      })
    })

    return Array.from(indicators).map(indicator => ({
      value: indicator,
      label: t(`indicator.${indicator}`),
    }))
  }

  const handleModeChange = (value: string) => {
    const mode = value as 'all' | 'single'
    setExportMode(mode)
    setValue('exportMode', mode)
    if (mode === 'all') {
      setValue('indicator', undefined)
    }
  }

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        {t('generic.maxDateRangeNote')}
      </Text>
      <Group>
        <InputDatetimePicker
          placeholder={capitalizeFirstLetter(t('generic.dateFrom'))}
          name="fromDate"
          maxDate={dayjs().toDate()}
          clearable
          icon={<BsCalendar2Date />}
          popoverProps={{
            withinPortal: true,
            zIndex: 10000,
          }}
        />
        <InputDatetimePicker
          placeholder={capitalizeFirstLetter(t('generic.dateTo'))}
          name="toDate"
          maxDate={dayjs().toDate()}
          clearable
          icon={<BsCalendar2Date />}
          popoverProps={{
            withinPortal: true,
            zIndex: 10000,
          }}
        />
      </Group>
      <Radio.Group
        value={exportMode}
        onChange={handleModeChange}
        label={capitalizeFirstLetter(t('sensor.indicators'))}
      >
        <Group mt="xs">
          <Radio value="single" label={t('generic.single')} />
          <Radio value="all" label={t('generic.all')} />
        </Group>
      </Radio.Group>
      {exportMode === 'single' && (
        <InputAsyncSelect
          name="indicator"
          placeholder={capitalizeFirstLetter(t('sensor.indicator'))}
          label={capitalizeFirstLetter(t('sensor.indicator'))}
          icon={TiWeatherPartlySunny}
          queryKey={['dashboard-indicators', dashboard.id]}
          queryFn={getIndicatorOptions}
          clearable
          searchable
          withinPortal
          zIndex={10000}
        />
      )}
      <HookFormError />
      <Group justify="right">
        <Button variant="outline" onClick={() => closeAllModals()}>
          {t('generic.cancel')}
        </Button>
        <InputSubmit mt={0} value={t('generic.export')} />
      </Group>
    </Stack>
  )
}
